import React, {useEffect, useState} from 'react';
import {ResponseStatus} from "../../models/api-response.model";
import {GuestCompany, loadGuestCompanies, login} from "../../services/auth.service";
import {useAuth} from "../../contexts/auth.context";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {getDefaultRoute} from "../../utils/default-route.util";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Divider,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import {
    Business as BusinessIcon,
    Email as EmailIcon,
    Lock as LockIcon,
    Login as LoginIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

export default function Login() {
    const [formData, setFormData] = useState({
        company: '',
        username: '',
        password: ''
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const [companies, setCompanies] = useState<GuestCompany[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<GuestCompany | null>(null);
    const [error, setError] = useState('');
    const [inputType, setInputType] = useState<'email' | 'phone'>('email');
    const [showPassword, setShowPassword] = useState(false);

    const {login: authLogin, isAuthenticated, user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && user) {
            const defaultRoute = getDefaultRoute(user);
            navigate(defaultRoute, {replace: true});
        }
    }, [isAuthenticated, user, navigate]);
    
    // Check for silent logout (token expired)
    useEffect(() => {
        // Check if we arrived here due to a silent logout (token expiration)
        const state = location.state as { silent?: boolean } | undefined;
        if (state?.silent) {
            // Don't show any error message for silent logouts
            console.log('Silent logout detected - not showing error message');
            // Clear the state to prevent showing this on manual navigation to login
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    useEffect(() => {
        loadGuestCompanies().then(response => {
            if (response.status === ResponseStatus.SUCCESS) {
                setCompanies(response.body);
            }
        })
    }, []);

    // Detect input type based on content
    useEffect(() => {
        const emailRegex = /\S+@\S+\.\S+/;
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;

        if (formData.username) {
            if (emailRegex.test(formData.username)) {
                setInputType('email');
            } else if (phoneRegex.test(formData.username)) {
                setInputType('phone');
            }
        }
    }, [formData.username]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleCompanyChange = (event: any, newValue: GuestCompany | null) => {
        setSelectedCompany(newValue);
        setFormData(prev => ({
            ...prev,
            company: newValue ? newValue.id.toString() : ''
        }));
        // Clear error when user selects a company
        if (errors.company) {
            setErrors(prev => ({...prev, company: ''}));
        }
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.company) {
            newErrors.company = 'Please select a company';
        }

        if (!formData.username) {
            newErrors.username = 'Email or phone number is required';
        } else {
            // Check if it's a valid email or a valid phone number
            const isValidEmail = /\S+@\S+\.\S+/.test(formData.username);
            const isValidPhone = /^\d{10,15}$/.test(formData.username.replace(/[^\d]/g, ''));

            if (!isValidEmail && !isValidPhone) {
                newErrors.username = 'Please enter a valid email address or phone number';
            }
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await login({
                username: formData.username,
                password: formData.password,
                companyId: parseInt(formData.company)
            });

            setIsLoading(false);

            if (response.status === ResponseStatus.SUCCESS && response.body) {
                // Cache the login response and update auth state
                authLogin(response.body);

                // Check if user needs to change password
                if (response.body.forcePasswordChange) {
                    // Redirect to password change page
                    navigate('/change-password', {replace: true});
                } else {
                    // Redirect to the default route based on user roles
                    const defaultRoute = getDefaultRoute(response.body);
                    navigate(defaultRoute, {replace: true});
                }
            } else if (response.status !== ResponseStatus.SUCCESS) {
                setError(response.message || 'Login failed. Please check your credentials and try again.');
            } else {
                setError('Login failed. Please check your credentials and try again.');
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Login error:', error);
            setError('Network error. Please check your connection and try again.');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Container maxWidth="sm">
                <Card
                    sx={{
                        maxWidth: 500,
                        mx: 'auto',
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    }}
                >
                    <CardContent sx={{p: 4}}>
                        <Box sx={{textAlign: 'center', mb: 4}}>
                            <LoginIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'primary.main',
                                    mb: 2,
                                }}
                            />
                            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                                Welcome Back
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Please sign in to your account
                            </Typography>
                        </Box>

                        <Box component="form" onSubmit={handleSubmit}
                             sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
                            {/* Display validation errors */}
                            {Object.keys(errors).length > 0 && (
                                <Alert severity="error" sx={{borderRadius: 2}}>
                                    <ul style={{margin: 0, paddingLeft: '20px'}}>
                                        {Object.entries(errors).map(([field, message]) => (
                                            <li key={field}>{message}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            <Autocomplete
                                options={companies}
                                getOptionLabel={(option) => option.name}
                                value={selectedCompany}
                                onChange={handleCompanyChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Company"
                                        placeholder="Search and select your company"
                                        error={!!errors.company}
                                        helperText={errors.company || "Choose the company you work for"}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <BusinessIcon color="action"/>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => {
                                    const {key, ...other} = props;
                                    return (
                                        <li key={key} {...other}>
                                            <BusinessIcon sx={{mr: 2, color: 'text.secondary'}}/>
                                            {option.name}
                                        </li>
                                    );
                                }}
                                noOptionsText="No companies found"
                                sx={{width: '100%'}}
                            />

                            <TextField
                                fullWidth
                                id="username"
                                name="username"
                                label="Email or Phone Number"
                                variant="outlined"
                                value={formData.username}
                                onChange={handleInputChange}
                                placeholder="Enter your email or phone number"
                                required
                                error={!!errors.username}
                                helperText={errors.username || "Use the email or phone number from your employee profile"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {inputType === 'email' ? (
                                                <EmailIcon color="action"/>
                                            ) : inputType === 'phone' ? (
                                                <PhoneIcon color="action"/>
                                            ) : (
                                                <PersonIcon color="action"/>
                                            )}
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                id="password"
                                name="password"
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter your password"
                                required
                                error={!!errors.password}
                                helperText={errors.password || "Enter the password you created during account setup"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color="action"/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Box
                                                onClick={() => setShowPassword(!showPassword)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: 'action.active',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            >
                                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </Box>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            {error && (
                                <Alert severity="error" sx={{mb: 2, borderRadius: 2}}>
                                    {error}
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                startIcon={<LoginIcon/>}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                                        boxShadow: '0 4px 8px 3px rgba(33, 203, 243, .4)',
                                    },
                                }}
                            >
                                {isLoading ? 'Signing in...' : 'Sign In'}
                            </Button>

                            <Divider sx={{my: 1.5}}>
                                <Typography variant="body2" color="text.secondary">
                                    OR
                                </Typography>
                            </Divider>

                            <Box sx={{textAlign: 'center'}}>
                                <Typography variant="body2">
                                    Don't have an account?{' '}
                                    <Link to="/signup"
                                          style={{textDecoration: 'none', color: 'primary.main', fontWeight: 600}}>
                                        Create Account
                                    </Link>
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}
